import React, { useContext, useEffect, useMemo, useState } from "react";

import { makeGet, signOut } from "@culturehq/client";
import { datadogRum } from "@datadog/browser-rum";

import { errorService } from "app/utils";

import * as CHQ from "chq";

const makeRefreshProfile = (setProfile: (_profile: CHQ.Profile) => void) => async () => {
  try {
    const { user: profile } = await makeGet<{ user: CHQ.Profile }>("/profile");
    setProfile(profile);
  } catch (error) {
    if ((error as any).status === 403) {
      await signOut();
      errorService.log("Redirect to login, profile context");
      window.location.replace(`${window.location.origin}/login`);
    } else {
      errorService.error(error);
    }
  }
};

export type DefaultProfile = Omit<Partial<CHQ.Profile>, "avatar" | "organization"> & {
  avatar: CHQ.Profile["avatar"];
  organization: Partial<CHQ.Organization>;
};

type ProfileState = {
  profile: DefaultProfile | CHQ.Profile;
  isPublic: boolean;
  fetchedProfile: (_profile: DefaultProfile | CHQ.Profile) => void;
  refreshProfile: () => void;
};

export const defaultProfile: DefaultProfile = {
  avatar: {
    thumbUrl: "http://via.placeholder.com/100x100",
    mediumUrl: "http://via.placeholder.com/500x500"
  },
  organization: {}
};

const ProfileContext = React.createContext<ProfileState>({
  profile: defaultProfile,
  isPublic: !defaultProfile.id,
  fetchedProfile: () => {},
  refreshProfile: () => {}
});

type ProfileProviderProps = {
  children: React.ReactNode;
  initialProfile?: CHQ.Profile;
};

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
  initialProfile = defaultProfile
}) => {
  const [profile, setProfile] = useState(initialProfile);
  const value = useMemo(
    () => ({
      profile,
      isPublic: !profile.id,
      fetchedProfile: setProfile,
      refreshProfile: makeRefreshProfile(setProfile)
    }),
    [profile]
  );

  useEffect(() => {
    if (!value.isPublic) {
      datadogRum.setUser({
        id: `${value.profile.id}`,
        name: value.profile.name,
        email: value.profile.email
      });
    }
  }, [value]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);

export default ProfileContext;
